'use client';

import React, { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Play, Trash2 } from 'lucide-react';

import { useReactPdf } from '@kit/documents/hooks';
import { withPosterFragment } from '@kit/shared/video-poster';
import { Button } from '@kit/ui/button';
import { Spinner } from '@kit/ui/spinner';
import { cn } from '@kit/ui/utils';

import type { MediaFile } from '../hooks/use-media-upload';
import { resolveVideoSource } from '../lib/video-source';
import { MuxHlsPlayer } from './mux-hls-player';

export type MediaCategory = 'image' | 'video' | 'document';

const IMAGE_EXTS = /\.(jpe?g|png|webp|avif|gif|heic|heif)$/i;
const VIDEO_EXTS = /\.(mp4|mov|webm|m4v)$/i;

/**
 * Infer media category from whatever signal is available.
 * Some loaders drop `mimeType`, so fall back to filename extension and
 * finally to a sensible default ("image") when a renderable URL exists.
 */
export function inferCategory(file: MediaFile): MediaCategory {
  const mt = file.mimeType?.toLowerCase();
  if (mt?.startsWith('image/')) return 'image';
  if (mt?.startsWith('video/')) return 'video';
  if (mt === 'application/pdf') return 'document';

  const name = file.fileName?.toLowerCase() ?? '';
  const path = file.storagePath?.toLowerCase() ?? '';
  if (IMAGE_EXTS.test(name) || IMAGE_EXTS.test(path)) return 'image';
  if (VIDEO_EXTS.test(name) || VIDEO_EXTS.test(path)) return 'video';

  // If any renderable URL is present, default to image — it's the most
  // common case in the wizard and at worst yields a broken-image icon
  // (still better than rendering a gallery as opaque "file" tiles).
  if (file.preview || file.signedUrl) return 'image';
  return 'document';
}

const PDF_EXT = /\.pdf$/i;

/**
 * True when the file is a PDF (by MIME or extension). PDFs render natively in
 * an `<iframe>`/`<embed>` — Supabase serves them `content-disposition: inline`
 * — so both the tile and the lightbox can show a real preview instead of a
 * generic file icon. Non-PDF documents have no universal inline renderer, so
 * they still fall back to the placeholder.
 */
export function isPdf(file: MediaFile): boolean {
  if (file.mimeType?.toLowerCase() === 'application/pdf') return true;
  const name = file.fileName?.toLowerCase() ?? '';
  const path = file.storagePath?.toLowerCase() ?? '';
  return PDF_EXT.test(name) || PDF_EXT.test(path);
}

/**
 * Ordered list of renderable URLs to try for a thumbnail, most-specific first.
 *
 * Order: explicit thumbnail → preview → signedUrl → storagePath (only when
 * it looks like a renderable URL). For private buckets, the bare
 * `storagePath` is just a key (e.g. `headshots/abc.jpg`) — the browser
 * cannot resolve it and would render a broken-image icon. Skip it in that
 * case so MediaThumb's load-error fallback kicks in cleanly.
 *
 * `ImageThumb` walks this list on the image element's onError, so an expired `signedUrl`
 * degrades to the next candidate before the neutral placeholder — rather than
 * failing straight to the placeholder on the first bad source.
 */
export function thumbnailSources(file: MediaFile): string[] {
  const isRenderable = (u: string) =>
    /^(https?:|blob:|data:)/.test(u) || u.startsWith('/');
  // EVERY candidate must be a browser-renderable URL. A bare private-bucket
  // storage KEY (e.g. `<uuid>/headshot/x.jpg`) in ANY field renders a
  // broken-image icon. This guards `signedUrl` and `thumbnailUrl` too, not
  // just `storagePath`: a loader that overloads a field with a signed URL
  // silently degrades to the raw key on a signing miss (getMediaByUser
  // returns the row unsigned — BAL-658), so validate here and drop it rather
  // than hand a raw key to an image element's src.
  const pick = (u: string | null | undefined) =>
    u && isRenderable(u) ? u : null;
  const candidates = [
    pick(file.thumbnailUrl),
    pick(file.preview),
    pick(file.signedUrl),
    pick(file.storagePath),
  ];
  return candidates.filter(
    (u, i): u is string => Boolean(u) && candidates.indexOf(u) === i,
  );
}

/**
 * Pick the single best URL to render (first candidate in the precedence chain).
 * Retained for the video path, which has no multi-source retry.
 */
export function thumbnailSrc(file: MediaFile): string | null {
  return thumbnailSources(file)[0] ?? null;
}

interface MediaThumbProps {
  file: MediaFile;
  onClick?: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
  /** Render variant — `single` is sized 12rem; `grid` fills the parent grid cell. */
  variant?: 'single' | 'grid';
  className?: string;
  deleteLabel?: string;
  /** AT label fallback used when the file has no name (e.g. "image preview"). */
  previewLabel?: (category: MediaCategory) => string;
  /** AT label for the video element. */
  videoPreviewLabel?: string;
}

/**
 * Square aspect-ratio media tile.
 *
 * Shows the asset (image | first-frame of video | document icon) with
 * hover/focus-revealed Delete button. Clicking the tile opens the lightbox
 * (parent wires `onClick`).
 */
export const MediaThumb = React.memo(function MediaThumb({
  file,
  onClick,
  onDelete,
  isDeleting,
  variant = 'grid',
  className,
  deleteLabel = 'Delete',
  previewLabel = (category) => `${category} preview`,
  videoPreviewLabel = 'Video preview',
}: MediaThumbProps) {
  const category = inferCategory(file);
  const sources = thumbnailSources(file);
  const interactive = Boolean(onClick) && !isDeleting;

  return (
    <div
      className={cn(
        'group bg-muted/40 ring-border/60 relative aspect-square overflow-hidden rounded-md ring-1 transition-shadow',
        variant === 'single' && 'h-48 w-48',
        interactive &&
          'hover:ring-foreground/40 focus-visible:ring-primary cursor-pointer focus-visible:ring-2 focus-visible:outline-none',
        className,
      )}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      aria-label={file.fileName ?? previewLabel(category)}
    >
      {/* Asset surface */}
      <ThumbAsset
        file={file}
        category={category}
        sources={sources}
        videoPreviewLabel={videoPreviewLabel}
      />

      {/* Bottom gradient + filename — only when the file has a name */}
      {file.fileName && variant === 'grid' && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <p className="truncate text-[11px] font-medium text-white/90">
            {file.fileName}
          </p>
        </div>
      )}

      {/* Delete button (hover/focus reveal) */}
      {onDelete && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isDeleting}
          aria-label={deleteLabel}
          className="hover:bg-destructive absolute top-1.5 right-1.5 size-7 rounded-full bg-black/60 text-white opacity-0 backdrop-blur-sm transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:text-white focus-visible:opacity-100"
        >
          {isDeleting ? (
            <Spinner className="size-3.5" />
          ) : (
            <Trash2 className="size-3.5" />
          )}
        </Button>
      )}
    </div>
  );
});

MediaThumb.displayName = 'MediaThumb';

function ThumbAsset({
  file,
  category,
  sources,
  videoPreviewLabel,
}: {
  file: MediaFile;
  category: MediaCategory;
  sources: string[];
  videoPreviewLabel: string;
}) {
  const firstSrc = sources[0] ?? null;

  if (category === 'image' && sources.length > 0) {
    return (
      // Key on the source list so a re-signed/refreshed source remounts and
      // resets the fallback index (an exhausted chain can recover on refresh).
      <ImageThumb
        key={sources.join('|')}
        sources={sources}
        fileName={file.fileName}
      />
    );
  }

  if (category === 'video') {
    // Route through the shared resolver (BAL-721 / video-playback-resolver): a
    // migrated video plays from its live source (Mux HLS / R2 CDN) instead of
    // the dead Supabase-Storage `signedUrl` that produced the apply-preview 400.
    const videoSource = resolveVideoSource({
      externalUrl: file.externalUrl,
      uploadedUrl: firstSrc,
      muxPlaybackId: file.muxPlaybackId,
      muxStatus: file.muxStatus,
      r2Key: file.r2Key,
    });

    if (videoSource.kind === 'mux' && file.id) {
      return (
        <MuxVideoThumb
          mediaId={file.id}
          fileName={file.fileName}
          videoPreviewLabel={videoPreviewLabel}
        />
      );
    }

    if (videoSource.kind === 'processing') {
      return <ProcessingThumb fileName={file.fileName} />;
    }

    const nativeSrc =
      videoSource.kind === 'r2' || videoSource.kind === 'native'
        ? videoSource.url
        : firstSrc;

    if (nativeSrc) {
      return (
        <VideoThumb
          src={nativeSrc}
          fileName={file.fileName}
          videoPreviewLabel={videoPreviewLabel}
        />
      );
    }
  }

  // PDF: render a page-1 thumbnail via react-pdf (see PdfThumb). NOT an
  // <iframe> — the app's CSP `frame-src` allows only youtube/vimeo/vercel, so
  // framing a same-origin PDF is blocked; react-pdf fetches the bytes over
  // connect-src instead and draws page 1 to a canvas.
  if (category === 'document' && firstSrc && isPdf(file)) {
    return <PdfThumb src={firstSrc} fileName={file.fileName} />;
  }

  // Non-PDF document, unknown type, external-only video, or an image whose URLs
  // all failed to load: a neutral file tile.
  return <PlaceholderTile fileName={file.fileName} isPdf={isPdf(file)} />;
}

/**
 * Image thumbnail with a graceful multi-source fallback.
 *
 * On a load error we advance to the next candidate in the precedence chain
 * (`preview` → `signedUrl` → `storagePath`) before giving up. Only when every
 * source is exhausted do we swap to PlaceholderTile — the browser's default
 * broken-image icon (with the filename as alt text) looks like a bug to users.
 * This lets an expired signed URL degrade to the next source instead of
 * failing straight to the placeholder.
 */
function ImageThumb({
  sources,
  fileName,
}: {
  sources: string[];
  fileName?: string;
}) {
  const [index, setIndex] = useState(0);
  const src = sources[index];

  if (!src) {
    return <PlaceholderTile fileName={fileName} />;
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element -- Supabase signed URLs not on Next image domains
    <img
      // Key on src so a source change remounts the image element and re-fires load.
      key={src}
      src={src}
      alt={fileName ?? ''}
      loading="lazy"
      decoding="async"
      onError={() => setIndex((i) => i + 1)}
      className="absolute inset-0 size-full object-cover"
    />
  );
}

/**
 * Neutral placeholder tile shown for documents, unknown types, and
 * images whose URL couldn't load. Always renders inside the same
 * aspect-square wrapper so the layout never shifts. For PDFs it shows a small
 * "PDF" badge so the file type reads at a glance (the full render is in the
 * lightbox).
 */
function PlaceholderTile({
  fileName,
  isPdf = false,
}: {
  fileName?: string;
  isPdf?: boolean;
}) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4">
      {isPdf && (
        <div className="pointer-events-none absolute top-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-white/90 backdrop-blur-sm">
          PDF
        </div>
      )}
      <FileText className="text-muted-foreground size-8" strokeWidth={1.5} />
      {fileName && (
        <p className="text-muted-foreground line-clamp-2 text-center text-[11px] break-all">
          {fileName}
        </p>
      )}
    </div>
  );
}

/**
 * PDF thumbnail: draws page 1 to a canvas via react-pdf (pdf.js). We do NOT use
 * an `<iframe>` — the app CSP `frame-src` blocks same-origin PDFs; react-pdf
 * fetches the bytes over connect-src instead. The portrait page renders at the
 * tile's width and is clipped by the square wrapper's `overflow-hidden`, so the
 * top of the document shows as the thumbnail. Any load/render error degrades to
 * the neutral PDF placeholder tile.
 *
 * react-pdf is heavier than an icon, which is fine here: PDF tiles are rare
 * (mostly a single CV upload), and pdf.js loads once and is shared.
 */
function PdfThumb({ src, fileName }: { src: string; fileName?: string }) {
  const { PdfComponents, isLoading } = useReactPdf(true);
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (ref.current) setWidth(ref.current.clientWidth || 192);
  }, []);

  if (failed) return <PlaceholderTile fileName={fileName} isPdf />;

  return (
    <div
      ref={ref}
      className="pointer-events-none absolute inset-0 overflow-hidden bg-white"
    >
      {(isLoading || !PdfComponents) && (
        <div className="absolute inset-0 grid place-items-center">
          <Spinner className="size-5" />
        </div>
      )}
      {PdfComponents && width && (
        <PdfComponents.Document
          file={src}
          loading={null}
          error={null}
          onLoadError={() => setFailed(true)}
        >
          <PdfComponents.Page
            pageNumber={1}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onRenderError={() => setFailed(true)}
          />
        </PdfComponents.Document>
      )}
      <div className="absolute top-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-white/90 backdrop-blur-sm">
        PDF
      </div>
    </div>
  );
}

/**
 * Video thumbnail: renders the first frame as poster via `<video preload="metadata">`,
 * with a centered Play overlay and a duration badge. No JS needed for poster
 * extraction — the browser does it for free.
 */
function VideoThumb({
  src,
  fileName,
  videoPreviewLabel,
}: {
  src: string;
  fileName?: string;
  videoPreviewLabel: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => {
      if (Number.isFinite(el.duration)) setDuration(el.duration);
    };
    el.addEventListener('loadedmetadata', handler);
    return () => el.removeEventListener('loadedmetadata', handler);
  }, []);

  return (
    <>
      <video
        ref={ref}
        src={withPosterFragment(src)}
        preload="metadata"
        muted
        playsInline
        aria-label={fileName ?? videoPreviewLabel}
        className="absolute inset-0 size-full object-cover"
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-background/80 grid size-10 place-items-center rounded-full shadow-md backdrop-blur-sm">
          <Play className="text-foreground size-4 translate-x-[1px]" />
        </div>
      </div>
      {duration !== null && (
        <div className="bg-background/85 text-foreground absolute bottom-1.5 left-1.5 rounded px-1.5 py-0.5 font-mono text-[10px] tracking-wider tabular-nums">
          {formatDuration(duration)}
        </div>
      )}
    </>
  );
}

function formatDuration(seconds: number): string {
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Mux video thumbnail: a signed HLS `<video>` (via MuxHlsPlayer) used as a
 * poster — the hook sets `video.poster` to the Mux still and preloads metadata,
 * so the tile shows a real first frame instead of the dead Supabase-Storage URL
 * that 400s for migrated videos (BAL-721). Controls off; clicking the tile
 * opens the lightbox (the parent wires `onClick`).
 */
function MuxVideoThumb({
  mediaId,
  fileName,
  videoPreviewLabel,
}: {
  mediaId: string;
  fileName?: string;
  videoPreviewLabel: string;
}) {
  return (
    <>
      <MuxHlsPlayer
        mediaId={mediaId}
        controls={false}
        muted
        playsInline
        aria-label={fileName ?? videoPreviewLabel}
        className="absolute inset-0 size-full bg-black object-cover"
      />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="bg-background/80 grid size-10 place-items-center rounded-full shadow-md backdrop-blur-sm">
          <Play className="text-foreground size-4 translate-x-[1px]" />
        </div>
      </div>
    </>
  );
}

/**
 * Processing state for a video whose Mux encode is still running
 * (`mux_status='preparing'`). Never flash a broken native `.mov` while it
 * encodes (R4).
 */
function ProcessingThumb({ fileName }: { fileName?: string }) {
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4"
      aria-label={fileName ? `${fileName} — processing` : 'processing'}
    >
      <Loader2 className="text-muted-foreground size-6 animate-spin" />
      {fileName && (
        <p className="text-muted-foreground line-clamp-2 text-center text-[11px] break-all">
          {fileName}
        </p>
      )}
    </div>
  );
}
